import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import api from "../../core/axios/axiosInstance";
import { message as antMessage } from "antd";

// ── Password strength helper (matches register page) ──────────────────────────
const getPasswordStrength = (password) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 1) return { label: "Weak",   color: "#ef4444", value: 25 };
  if (score === 2) return { label: "Medium", color: "#f59e0b", value: 50 };
  if (score === 3) return { label: "Good",   color: "#06b6d4", value: 75 };
  return              { label: "Strong", color: "#22c55e", value: 100 };
};

const AgentSetup = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verificationToken = searchParams.get("verificationToken");

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (!verificationToken) {
      antMessage.error("Invalid verification link");
      navigate(routes.login);
    }
  }, [verificationToken, navigate, routes.login]);

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user types
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
      isValid = false;
    } else if (
      !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])/.test(formData.password)
    ) {
      newErrors.password =
        "Password must contain uppercase, lowercase, number and special character";
      isValid = false;
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
      isValid = false;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post("/admin/user/verify", {
        verifyToken: verificationToken,
        password: formData.password,
      });

      if (response.data.status === "success") {
        antMessage.success("Account setup successful! Logging you in...");

        // Store the JWT token
        const token = response.data.data.token;
        localStorage.setItem("token", token);

        // Redirect to agent dashboard
        setTimeout(() => {
          navigate(routes.agentDashboard);
          window.location.reload(); // Reload to initialize auth state
        }, 1000);
      } else {
        antMessage.error(response.data.message || "Setup failed");
      }
    } catch (error) {
      console.error("Agent setup error:", error);
      antMessage.error(
        error.response?.data?.message ||
          "Failed to complete setup. Please try again or contact support."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="account-content">
      <div className="d-flex flex-wrap w-100 vh-100 overflow-hidden ">
        <div className="d-flex align-items-center justify-content-center flex-wrap vh-100 overflow-auto p-4 w-50 bg-backdrop">
          <form className="flex-fill" onSubmit={handleSubmit}>
            <div className="mx-auto mw-450">
              <div className="text-center mb-4">
                <Link to={"https://voycell.com"}>
                <ImageWithBasePath
                  src="assets/img/voycell-logo.webp"
                  className="img-fluid"
                  alt="Logo"
                />
                </Link>
              </div>
              <div className="mb-4">
                <h4 className="mb-2 fs-20">Complete Your Agent Account Setup</h4>
                <p>Create a secure password for your agent account</p>
              </div>

              <div className="mb-3">
                <label className="col-form-label">Password</label>
                <div className="pass-group">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    className={`pass-input form-control ${
                      errors.password ? "is-invalid" : ""
                    }`}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="*********"
                    disabled={isSubmitting}
                  />
                  <span
                    className={`ti toggle-passwords ${
                      passwordVisible ? "ti-eye" : "ti-eye-off"
                    }`}
                    onClick={togglePasswordVisibility}
                  ></span>
                </div>
                {errors.password && (
                  <div className="text-danger mt-1 small">
                    {errors.password}
                  </div>
                )}
                {/* Password strength bar */}
                {formData.password && (() => {
                  const strength = getPasswordStrength(formData.password);
                  return (
                    <div className="mt-2">
                      <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                        {[25, 50, 75, 100].map((threshold) => (
                          <div
                            key={threshold}
                            style={{
                              flex: 1, height: 4, borderRadius: 4,
                              background: strength.value >= threshold ? strength.color : "#e5e7eb",
                              transition: "background 0.3s",
                            }}
                          />
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: strength.color, fontWeight: 600 }}>
                        {strength.label} password
                      </div>
                    </div>
                  );
                })()}
                {!formData.password && (
                  <div className="mt-2 small text-muted">
                    Must be 8+ chars with uppercase, lowercase, number &amp; special character
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">Confirm Password</label>
                <div className="pass-group">
                  <input
                    type={confirmPasswordVisible ? "text" : "password"}
                    className={`pass-input form-control ${
                      errors.confirmPassword ? "is-invalid" : ""
                    }`}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="*********"
                    disabled={isSubmitting}
                  />
                  <span
                    className={`ti toggle-passwords ${
                      confirmPasswordVisible ? "ti-eye" : "ti-eye-off"
                    }`}
                    onClick={toggleConfirmPasswordVisibility}
                  ></span>
                </div>
                {errors.confirmPassword && (
                  <div className="text-danger mt-1 small">
                    {errors.confirmPassword}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Setting up...
                    </>
                  ) : (
                    "Complete Setup"
                  )}
                </button>
              </div>

              <div className="mb-3">
                <h6>
                  Already have an account?{" "}
                  <Link to={routes.login} className="text-purple link-hover">
                    {" "}
                    Sign In
                  </Link>
                </h6>
              </div>

              <div className="text-center">
                <p className="fw-medium text-gray">
                  Copyright © 2026 - VOYCELL
                </p>
              </div>
            </div>
          </form>
        </div>
        <div className="d-flex flex-wrap w-50 vh-100  account-bg-02"></div>
      </div>
    </div>
  );
};

export default AgentSetup;
